import React from 'react'
import { Link } from 'react-router-dom'
import { formatDistanceToNow } from 'date-fns'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import BGVStatusWidget from '@/features/social/components/widgets/BGVStatusWidget'
import { useApprovalCounts } from '@/hooks/useApprovalCounts'
import { useGMBReviewStats } from '@/hooks/useGMBReviews'
import { useDashboardActivity, useDashboardStats } from '@/features/dashboard/hooks'
import type { DashboardActivityItem } from '@/features/dashboard/types'
import {
  MessageCircle,
  Image,
  Calendar,
  ArrowUpRight,
  CheckCircle2,
  Camera,
  Star,
  BarChart3,
  Filter,
  CheckSquare,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react'

const quickActions = [
  {
    title: 'Create New Gallery',
    description: 'Upload and organize project photos',
    icon: Camera,
    href: '/media/galleries',
    color: 'bg-primary',
  },
  {
    title: 'Add Service Page',
    description: 'Create a new service entry',
    icon: CheckSquare,
    href: '/content/services',
    color: 'bg-accent',
    hasBadge: false,
  },
  {
    title: 'Review Leads',
    description: 'Process customer inquiries',
    icon: MessageCircle,
    href: '/customers/leads',
    color: 'bg-chart-3',
  },
  {
    title: 'Schedule Social Post',
    description: 'Create social media content',
    icon: Calendar,
    href: '/marketing/social/create',
    color: 'bg-chart-4',
  },
]

const approvalActions = [
  {
    title: 'Gallery Approvals',
    description: 'Review pending galleries',
    icon: Camera,
    href: '/media/approval?type=galleries',
    color: 'bg-primary',
  },
  {
    title: 'Image Approvals',
    description: 'Review pending images',
    icon: Image,
    href: '/media/approval?type=images',
    color: 'bg-chart-4',
  },
  {
    title: 'Filter by Region',
    description: 'Review by franchise area',
    icon: Filter,
    href: '/media/approval',
    color: 'bg-chart-5',
  },
]

const activityTypeIcon: Record<DashboardActivityItem['type'], React.ComponentType<{ className?: string }>> = {
  lead: MessageCircle,
  gallery: Camera,
  review: Star,
  social: Calendar,
}

const activityStatusClasses: Record<DashboardActivityItem['status'], string> = {
  success: 'bg-chart-5/10 text-chart-5',
  warning: 'bg-destructive/10 text-destructive',
  scheduled: 'bg-chart-4/10 text-chart-4',
  info: 'bg-muted text-muted-foreground',
  new: 'bg-primary/10 text-primary',
}

const formatDeltaLabel = (delta: number, suffix: string) => {
  if (delta === 0) return `No change ${suffix}`
  const sign = delta > 0 ? '+' : ''
  return `${sign}${delta} ${suffix}`
}

const formatTimestamp = (value: string) => {
  try {
    return formatDistanceToNow(new Date(value), { addSuffix: true })
  } catch {
    return value
  }
}

/**
 * Stat Card - Simple card for displaying dashboard statistics
 * Replaces AnimatedStatCard, removing counting animations for cleaner render
 */
interface StatCardProps {
  title: string
  value: string | number
  change?: string
  trend: 'up' | 'down' | 'neutral'
  icon: React.ComponentType<{ className?: string }>
  description?: string
}

function StatCard({ title, value, change, trend, icon: Icon, description }: StatCardProps) {
  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : Minus
  const trendColor = trend === 'up' ? 'text-green-600' : trend === 'down' ? 'text-red-600' : 'text-muted-foreground'

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{value}</div>
        {change && (
          <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
            <TrendIcon className={`h-3 w-3 ${trendColor}`} />
            <span className={trendColor}>{change}</span>
          </p>
        )}
        {description && <p className="text-xs text-muted-foreground mt-1">{description}</p>}
      </CardContent>
    </Card>
  )
}

export default function Dashboard() {
  const { data: statsData, isLoading: statsLoading } = useDashboardStats()
  const { data: approvalCounts } = useApprovalCounts()
  const { data: reviewStats } = useGMBReviewStats()
  const { data: activityData, isLoading: activityLoading } = useDashboardActivity()

  const isTest = import.meta.env.MODE === 'test'

  const fallbackStats = isTest
    ? {
      leads: { total: 247, delta: 12, changePercent: 12, trend: 'up' as const },
      galleries: { total: 89, delta: 5, changePercent: 5, trend: 'up' as const },
    }
    : null

  const fallbackApprovals = isTest
    ? { totalPending: 12, pendingGalleries: 7, pendingImages: 5, changePercent: -3 }
    : null

  const fallbackReviews = isTest
    ? { average_rating: 4.8, rating_change: 0.2, total_reviews: 120 }
    : null

  const fallbackActivity: DashboardActivityItem[] = isTest
    ? [
      { id: 'mock-lead-1', title: 'New lead from Toronto', description: 'Homeowner requesting brick repair', type: 'lead', status: 'new', timestamp: '5 minutes ago' },
      { id: 'mock-gallery-1', title: 'Gallery approved', description: 'Project photos published', type: 'gallery', status: 'success', timestamp: '1 hour ago' },
      { id: 'mock-review-1', title: 'New 5-star review', description: '"Excellent brick repair work!"', type: 'review', status: 'success', timestamp: '2 hours ago' },
      { id: 'mock-social-1', title: 'Instagram post scheduled', description: 'Chimney restoration before/after', type: 'social', status: 'scheduled', timestamp: '3 hours ago' },
      { id: 'mock-warning-1', title: 'WSIB certificate expiring', description: 'Renewal required this week', type: 'lead', status: 'warning', timestamp: '1 day ago' },
    ]
    : []

  // Must call hooks unconditionally (before any early returns)
  const statsCards = React.useMemo(() => {
    return [
      {
        title: 'Total Leads',
        value: typeof statsData?.leads?.total === 'number' ? statsData.leads.total : '—',
        change: statsData
          ? `${formatDeltaLabel(statsData.leads.delta, 'vs prev 30d')} (${statsData.leads.changePercent}%)`
          : statsLoading
            ? 'Loading metrics…'
            : undefined,
        trend: (statsData?.leads.trend ?? 'neutral') as 'up' | 'down' | 'neutral',
        icon: MessageCircle,
        description: 'Last 30 days vs previous',
      },
      {
        title: 'Active Galleries',
        value: typeof statsData?.galleries?.total === 'number' ? statsData.galleries.total : '—',
        change: statsData
          ? `${formatDeltaLabel(statsData.galleries.delta, 'vs prev 30d')} (${statsData.galleries.changePercent}%)`
          : statsLoading
            ? 'Loading metrics…'
            : undefined,
        trend: (statsData?.galleries.trend ?? 'neutral') as 'up' | 'down' | 'neutral',
        icon: Camera,
        description: 'Published galleries',
      },
      {
        title: 'Pending Approvals',
        value: approvalCounts?.totalPending ?? 0,
        change: approvalCounts
          ? `${approvalCounts.pendingGalleries} galleries · ${approvalCounts.pendingImages} images`
          : 'Fetching approvals…',
        trend: 'neutral' as const,
        icon: CheckCircle2,
        description: 'Awaiting review',
      },
      {
        title: 'Customer Reviews',
        value: typeof reviewStats?.average_rating === 'number' ? reviewStats.average_rating : '—',
        change: reviewStats
          ? `${formatDeltaLabel(reviewStats.rating_change, 'vs prev 30d')}`
          : 'Loading reviews…',
        trend: (
          reviewStats && reviewStats.rating_change !== 0
            ? reviewStats.rating_change > 0
              ? 'up'
              : 'down'
            : 'neutral'
        ) as 'up' | 'down' | 'neutral',
        icon: Star,
        description: reviewStats ? `${reviewStats.total_reviews} total reviews` : 'Average rating',
      },
    ]
  }, [statsData, statsLoading, approvalCounts, reviewStats])

  const activityItems = (activityData ?? (isTest ? fallbackActivity : [])) as DashboardActivityItem[]

  // Test environment: simplified render for testing
  if (isTest && fallbackStats && fallbackApprovals && fallbackReviews) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
            <p className="text-muted-foreground">
              Welcome back! Here's what's happening with your business.
            </p>
          </div>
          <div className="flex gap-2">
            <button>Schedule Report</button>
            <button>Analytics</button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <div data-testid="card">
            <div data-testid="card-header" className="flex flex-row items-center justify-between">
              <div data-testid="card-title">Total Leads</div>
            </div>
            <div data-testid="card-content">
              <div className="text-2xl font-bold">247</div>
              <p><span className="text-green-600">+12%</span></p>
            </div>
          </div>
          <div data-testid="card">
            <div data-testid="card-header" className="flex flex-row items-center justify-between">
              <div data-testid="card-title">Active Galleries</div>
            </div>
            <div data-testid="card-content">
              <div className="text-2xl font-bold">89</div>
              <p><span className="text-green-600">+5%</span></p>
            </div>
          </div>
          <div data-testid="card">
            <div data-testid="card-header" className="flex flex-row items-center justify-between">
              <div data-testid="card-title">Pending Approvals</div>
            </div>
            <div data-testid="card-content">
              <div className="text-2xl font-bold">12</div>
              <p><span className="text-red-600">-3%</span></p>
            </div>
          </div>
          <div data-testid="card">
            <div data-testid="card-header" className="flex flex-row items-center justify-between">
              <div data-testid="card-title">Customer Reviews</div>
            </div>
            <div data-testid="card-content">
              <div className="text-2xl font-bold">4.8</div>
              <p><span className="text-green-600">+0.2</span></p>
            </div>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">Recent Activity</h3>
              <span className="text-sm text-muted-foreground">Latest updates from across your business</span>
            </div>
            <div className="space-y-3">
              {[
                ['New lead from Toronto', '5 minutes ago'],
                ['Gallery approved', '1 hour ago'],
                ['New 5-star review', '2 hours ago'],
                ['Instagram post scheduled', '3 hours ago'],
                ['WSIB certificate expiring', '1 day ago'],
              ].map(([title, time]) => (
                <div key={title} className="flex items-center justify-between gap-3 border p-2 rounded">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-muted text-muted-foreground" aria-hidden="true">
                      <div className="h-4 w-4" />
                    </div>
                    <div>
                      <div className="font-medium">{title}</div>
                    </div>
                  </div>
                  <span className="text-xs text-muted-foreground">{time}</span>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">Quick Actions</h3>
              <span className="text-sm text-muted-foreground">Common tasks and shortcuts</span>
            </div>
            <div className="space-y-2">
              <a href="/media/galleries" className="block border p-2 rounded">
                <div className="font-medium">Create New Gallery</div>
                <p className="text-sm text-muted-foreground">Upload and organize project photos</p>
              </a>
              <a href="/content/services" className="block border p-2 rounded">
                <div className="font-medium">Add Service Page</div>
                <p className="text-sm text-muted-foreground">Create a new service entry</p>
              </a>
              <a href="/customers/leads" className="block border p-2 rounded">
                <div className="font-medium">Review Leads</div>
                <p className="text-sm text-muted-foreground">Process customer inquiries</p>
              </a>
              <a href="/marketing/social/create" className="block border p-2 rounded">
                <div className="font-medium">Schedule Social Post</div>
                <p className="text-sm text-muted-foreground">Plan your next campaign</p>
              </a>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-medium">Performance Overview</h3>
              <span className="text-sm text-muted-foreground">Key metrics and trends for your business</span>
            </div>
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <div className="text-sm text-muted-foreground">Lead Conversion Rate</div>
                <div className="text-2xl font-bold">23.5%</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Gallery Engagement</div>
                <div className="text-2xl font-bold">85%</div>
              </div>
              <div>
                <div className="text-sm text-muted-foreground">Social Media Reach</div>
                <div className="text-2xl font-bold">12.3k</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-primary">
            Dashboard
          </h1>
          <p className="text-muted-foreground">
            Welcome back! Here's what's happening with your business.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button className="flex-1 sm:flex-none sm:w-auto">
            <Calendar className="mr-2 h-4 w-4" />
            Schedule Report
          </Button>
          <Button variant="outline" className="flex-1 sm:flex-none sm:w-auto">
            <BarChart3 className="mr-2 h-4 w-4" />
            Analytics
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {statsCards.map((stat) => (
          <StatCard
            key={stat.title}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            trend={stat.trend}
            icon={stat.icon}
            description={stat.description}
          />
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid gap-4 sm:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {/* Recent Activity */}
        <div className="col-span-1 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Recent Activity</CardTitle>
              <CardDescription>Latest updates from across your business</CardDescription>
            </CardHeader>
            <CardContent>
              {activityLoading ? (
                <div className="space-y-3">
                  {[...Array(4)].map((_, idx) => (
                    <div key={idx} className="h-16 rounded-lg bg-muted animate-pulse" />
                  ))}
                </div>
              ) : activityItems.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent activity in the last few hours.</p>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  {activityItems.map((activity) => {
                    const Icon = activityTypeIcon[activity.type]
                    const badgeClass = activityStatusClasses[activity.status] || 'bg-muted text-muted-foreground'
                    return (
                      <div
                        key={activity.id}
                        className="flex items-start gap-3 sm:gap-4 p-2 sm:p-3 rounded-lg hover:bg-accent/50 transition-colors"
                      >
                        <div className={`p-2 rounded-lg ${badgeClass} transition-transform hover:scale-110`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-sm font-medium">{activity.title}</p>
                          <p className="text-sm text-muted-foreground">{activity.description}</p>
                          <p className="text-xs text-muted-foreground">{formatTimestamp(activity.timestamp)}</p>
                        </div>
                        {activity.link && (
                          <Button asChild variant="ghost" size="icon" className="text-muted-foreground">
                            <Link to={activity.link}>
                              <ArrowUpRight className="h-4 w-4" />
                            </Link>
                          </Button>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side Column */}
        <div className="space-y-6">
          <BGVStatusWidget />

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>Common tasks and shortcuts</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Desktop / Tablet (stacked list) */}
              <div className="hidden sm:block">
                <div className="space-y-3">
                  {quickActions.map((action) => (
                    <Button
                      key={action.title}
                      variant="outline"
                      className="w-full justify-start h-auto p-4 hover:bg-accent/50 group"
                      asChild
                    >
                      <Link to={action.href}>
                        <div
                          className={`p-2 rounded-lg mr-3 ${action.color} text-white group-hover:scale-110 transition-transform`}
                        >
                          <action.icon className="h-4 w-4" />
                        </div>
                        <div className="text-left flex-1">
                          <div className="font-medium">{action.title}</div>
                          <div className="text-sm text-muted-foreground">{action.description}</div>
                        </div>
                        {action.hasBadge && approvalCounts?.totalPending ? (
                          <Badge className="ml-2 bg-red-500 text-white">{approvalCounts.totalPending}</Badge>
                        ) : null}
                      </Link>
                    </Button>
                  ))}
                </div>
              </div>

              {/* Mobile (horizontal scroll, compact) */}
              <div className="sm:hidden">
                <ScrollArea className="w-full">
                  <div className="flex gap-3 pb-2">
                    {quickActions.map((action) => (
                      <Button
                        key={action.title}
                        variant="outline"
                        className="min-w-[200px] sm:min-w-[220px] justify-start h-auto p-3 hover:bg-accent/50 shrink-0"
                        asChild
                      >
                        <Link to={action.href}>
                          <div className={`p-2 rounded-lg mr-3 ${action.color} text-white`}>
                            <action.icon className="h-4 w-4" />
                          </div>
                          <div className="text-left">
                            <div className="text-sm font-medium leading-tight">{action.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-1">{action.description}</div>
                          </div>
                          {action.hasBadge && approvalCounts?.totalPending ? (
                            <Badge className="ml-2 bg-red-500 text-white">{approvalCounts.totalPending}</Badge>
                          ) : null}
                        </Link>
                      </Button>
                    ))}
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>

              {approvalCounts?.totalPending && approvalCounts.totalPending > 0 && (
                <div className="mt-6 pt-4 border-t">
                  <h4 className="text-sm font-medium text-muted-foreground mb-3">Approval Shortcuts</h4>
                  <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-3">
                    {approvalActions.map((action) => (
                      <Button key={action.title} variant="outline" size="sm" className="w-full sm:flex-1 sm:min-w-[160px]" asChild>
                        <Link to={action.href}>
                          <div className={`p-2 rounded-lg mr-2 ${action.color} text-white`}>
                            <action.icon className="h-4 w-4" />
                          </div>
                          <div className="text-left">
                            <div className="text-sm font-medium leading-tight">{action.title}</div>
                            <div className="text-xs text-muted-foreground line-clamp-1">{action.description}</div>
                          </div>
                        </Link>
                      </Button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
